/**
 * E07-F02-T17 — per-tile results of the discovery search.
 *
 * A request fans out into tiles (sub-industry × location × size). Before this the
 * request page said nothing per tile: 8 of 34 tiles added 0 rows and nobody could
 * tell why. The server now records one row per tile on the run
 * (`job.discoveryTiles`, research-orchestrator contract): found / duplicates / new,
 * the filters the search sent, and the tile's failure or zero-result notice. This
 * table shows it; a tile that added nothing states the reason in words
 * (`discoveryTileZeroReason`, shared with the API tests), a failed tile
 * (location_unresolved, provider errors) shows its error in the same row.
 *
 * Read-only: nothing here re-runs a search (no-automatic-AI / no-retry rule).
 *
 * E06-F05-T15: a technical block for algorithm debugging — collapsed by default under
 * "Technical details (raw)", hidden for non-admins by `./rawDetails.ts`.
 */
import type { JSX } from 'react';
import {
  describeDiscoveryTileFilters,
  discoveryTileZeroReason,
  isDiscoveryNotice,
  type DiscoveryTileStat,
} from '@eer/shared';
import { useResearchStatus } from './researchOrchestratorApi.js';
import { RAW_DETAILS_LABEL, useCanSeeRawDetails } from './rawDetails.js';

export interface DiscoveryTilesTableProps {
  tiles: readonly DiscoveryTileStat[];
  /**
   * Review screen spacing. Since E06-F05-T15 the table is ALWAYS a closed-by-default
   * "Technical details (raw)" disclosure; this flag only tightens the bottom margin.
   */
  collapsible?: boolean;
  testId?: string;
}

/** Totals of the tile table (header line). Pure. */
function discoveryTilesSummary(tiles: readonly DiscoveryTileStat[]): {
  searches: number;
  found: number;
  duplicates: number;
  created: number;
  zero: number;
  failed: number;
} {
  let found = 0;
  let duplicates = 0;
  let created = 0;
  let zero = 0;
  let failed = 0;
  for (const t of tiles) {
    found += t.found;
    duplicates += t.duplicates;
    created += t.created;
    if (t.created === 0) zero += 1;
    if (t.issue && !isDiscoveryNotice(t.issue)) failed += 1;
  }
  return { searches: tiles.length, found, duplicates, created, zero, failed };
}

const dash = (v: string | null): string => (v && v.trim() ? v : '—');

export function DiscoveryTilesTable({
  tiles,
  collapsible = false,
  testId = 'discovery-tiles',
}: DiscoveryTilesTableProps): JSX.Element | null {
  const canSeeRaw = useCanSeeRawDetails();
  if (!canSeeRaw || tiles.length === 0) return null;
  const s = discoveryTilesSummary(tiles);
  const summary = (
    <span data-testid="discovery-tiles-summary">
      {s.searches} {s.searches === 1 ? 'search' : 'searches'} · {s.found} found · {s.duplicates}{' '}
      duplicates · {s.created} new
      {s.zero > 0 ? ` · ${s.zero} added nothing` : ''}
      {s.failed > 0 ? ` · ${s.failed} failed` : ''}
    </span>
  );

  const table = (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">Sub-industry</th>
            <th className="px-3 py-2 text-left">Location</th>
            <th className="px-3 py-2 text-left">Size</th>
            <th className="px-3 py-2 text-right">Found</th>
            <th className="px-3 py-2 text-right">Duplicates</th>
            <th className="px-3 py-2 text-right">New</th>
            <th className="px-3 py-2 text-left">Filters</th>
            <th className="px-3 py-2 text-left">Result</th>
          </tr>
        </thead>
        <tbody>
          {tiles.map((t, i) => {
            const reason = discoveryTileZeroReason(t);
            const failed = t.issue != null && !isDiscoveryNotice(t.issue);
            return (
              <tr
                key={i}
                data-testid="discovery-tile-row"
                data-zero={t.created === 0 ? 'true' : 'false'}
                data-issue-code={t.issue?.code ?? ''}
                className={`border-t border-gray-100 ${
                  failed ? 'bg-red-50' : t.created === 0 ? 'bg-amber-50/60' : ''
                }`}
              >
                <td className="px-3 py-2 text-slate-800">{dash(t.tile.subIndustry)}</td>
                <td className="px-3 py-2 text-slate-800">{dash(t.tile.geoTile)}</td>
                <td className="px-3 py-2 text-slate-600">{dash(t.tile.sizeBand)}</td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid="tile-found">
                  {t.capped ? '—' : t.found}
                </td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid="tile-duplicates">
                  {t.duplicates}
                  {t.excluded > 0 ? (
                    <span
                      className="text-gray-400"
                      title="Cut at search: Do-Not-Source lists or outside the size window"
                    >
                      {' '}
                      +{t.excluded} excluded
                    </span>
                  ) : null}
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums font-bold ${
                    t.created > 0 ? 'text-emerald-700' : 'text-slate-400'
                  }`}
                  data-testid="tile-created"
                >
                  {t.created}
                </td>
                <td className="px-3 py-2 text-slate-500" data-testid="tile-filters">
                  {describeDiscoveryTileFilters(t.filters)}
                </td>
                <td
                  className={`px-3 py-2 ${failed ? 'text-red-700' : reason ? 'text-amber-800' : 'text-emerald-700'}`}
                  data-testid="tile-reason"
                >
                  {failed ? (
                    <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />
                  ) : null}
                  {reason ?? 'Added new companies'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // E06-F05-T15 — a technical block: always collapsed under the explicit raw label
  // (on Run too, where it used to be an open card), hidden by the one audience switch.
  return (
    <details
      data-testid={testId}
      data-raw-details="true"
      className={`border border-dashed border-gray-300 rounded-lg ${collapsible ? 'mb-4' : 'mb-6'} bg-white text-sm`}
    >
      <summary className="px-4 py-2 cursor-pointer text-slate-600">
        <span
          data-testid="raw-details-label"
          className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500"
        >
          <i className="fa-solid fa-wrench mr-1" aria-hidden />
          {RAW_DETAILS_LABEL}
        </span>
        <b className="text-slate-800">Search results by tile</b> — {summary}
      </summary>
      {table}
    </details>
  );
}

/** Review screen: the same table, collapsed, from the run snapshot. */
export function ReviewDiscoveryTiles({ requestId }: { requestId: number }): JSX.Element | null {
  const { data } = useResearchStatus(requestId);
  return (
    <DiscoveryTilesTable
      tiles={data?.job.discoveryTiles ?? []}
      collapsible
      testId="review-discovery-tiles"
    />
  );
}
